package com.evoapps.evoshift.domain.explain

import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.Payslip
import com.evoapps.evoshift.domain.model.EmployeePayroll
import com.evoapps.evoshift.domain.model.RosterPayroll

/*
 * SH-152: the labour cost screen explained, from the figures the payroll rules already worked out
 * ([EmployeePayroll]: the payslip, the month's pay factors, the adjustments): a person's gross
 * (the pay for the hours or the salary, overtime, Sunday and holiday supplements, the
 * minimum-wage top-up, leave pay, bonuses), the take-home (the gross less the person's
 * contributions and tax, less deductions, plus refunds) and the employer cost (the gross plus the
 * employer's contributions and PPK, less what ZUS pays, plus refunds). An estimate, as everywhere
 * on that screen. Nothing here recalculates pay: each term is a figure the payslip holds.
 */
object PayrollExplanations {

    private fun payslipOf(person: EmployeePayroll, side: Side): Payslip =
        if (side == Side.PLAN) person.plannedPayslip ?: person.payslip else person.payslip

    private fun factorsOf(person: EmployeePayroll, side: Side): MonthPayFactors? =
        if (side == Side.PLAN) person.plannedFactors ?: person.factors else person.factors

    /** A person whose figures say nothing about pay: without pay set, or working without pay. */
    private fun statusTerm(person: EmployeePayroll): Term? = when {
        person.missingPay -> Term(TermKind.PERSON_WITHOUT_PAY, 0.0, Sign.NOT_COUNTED, personId = person.userId, hours = person.plannedHours, shifts = person.shifts.size)
        person.worksWithoutPay -> Term(TermKind.PERSON_UNPAID, 0.0, personId = person.userId, hours = person.plannedHours, shifts = person.shifts.size)
        else -> null
    }

    /** What the month adds to the pay for the hours, one term each (none where 0). */
    private fun factorTerms(factors: MonthPayFactors?): List<Term> {
        val f = factors ?: return emptyList()
        return listOfNotNull(
            Term(TermKind.OVERTIME_PAY, f.overtimePay, hours = f.overtimeHours.takeIf { it > 0 }),
            Term(TermKind.OVERTIME_50, f.overtime50Supplement, hours = f.overtime50Hours.takeIf { it > 0 }),
            Term(TermKind.OVERTIME_100, f.overtime100Supplement, hours = f.overtime100Hours.takeIf { it > 0 }),
            Term(TermKind.SUNDAY_HOLIDAY, f.sundayHolidaySupplement, hours = f.sundayHolidayHours.takeIf { it > 0 }),
            Term(TermKind.MINIMUM_WAGE_TOP_UP, f.minimumWageTopUp),
            Term(TermKind.SICK_PAY, f.sickPay, days = f.sickPayDays.takeIf { it > 0 }),
            Term(TermKind.SICK_BENEFIT, f.sickBenefit, days = f.sickBenefitDays.takeIf { it > 0 }),
            Term(TermKind.HOLIDAY_PAY, f.holidayPay + f.holidayVariablePay, hours = f.vacationHours.takeIf { it > 0 }, days = f.vacationDays.takeIf { it > 0 }),
            Term(TermKind.SALARY_REDUCTION, f.salaryReduction, Sign.MINUS),
            f.rateChangeAdjustment.let { Term(TermKind.RATE_CHANGE, kotlin.math.abs(it), if (it < 0) Sign.MINUS else Sign.PLUS) }
        ).filter { it.amount != 0.0 }
    }

    /**
     * [person]'s gross of [side]: the pay for their hours (or the salary), what the month adds,
     * and the bonuses; the gross the payslip was worked out from.
     */
    fun gross(person: EmployeePayroll, side: Side): Breakdown {
        val payslip = payslipOf(person, side)
        statusTerm(person)?.let { return Breakdown.of(Figure.GROSS, side, payslip.gross, listOf(it)) }
        val factors = factorsOf(person, side)
        val base = payslip.gross - person.bonuses - (factors?.extraGross ?: 0.0)
        val hours = if (side == Side.PLAN) person.plannedHours else person.paidHours
        val terms = listOf(Term(TermKind.BASE_PAY, base, hours = hours, shifts = person.shifts.size.takeIf { it > 0 })) +
            factorTerms(factors) + listOf(Term(TermKind.BONUSES, person.bonuses))
        return Breakdown.of(Figure.GROSS, side, payslip.gross, terms)
    }

    /** [person]'s take-home of [side]: the gross less their contributions and tax, less deductions, plus refunds. */
    fun takeHome(person: EmployeePayroll, side: Side): Breakdown {
        val shown = if (side == Side.PLAN) person.plannedToPay else person.toPay
        statusTerm(person)?.let { return Breakdown.of(Figure.TAKE_HOME, side, shown, listOf(it)) }
        val payslip = payslipOf(person, side)
        val terms = buildList {
            add(gross(person, side).asTerm(TermKind.GROSS))
            payslip.employeeLines.forEach { add(Term(TermKind.EMPLOYEE_LINE, it.amount, Sign.MINUS, payLine = it.kind)) }
            add(Term(TermKind.DEDUCTIONS, person.deductions, Sign.MINUS))
            add(Term(TermKind.EXPENSE_REFUNDS, person.expenseRefunds))
        }
        return Breakdown.of(Figure.TAKE_HOME, side, shown, terms)
    }

    /** [person]'s employer cost of [side]: the gross plus the employer's contributions (less what ZUS pays) plus refunds. */
    fun employerCost(person: EmployeePayroll, side: Side): Breakdown {
        val shown = if (side == Side.PLAN) person.plannedEmployerCost else person.employerCost
        statusTerm(person)?.let { return Breakdown.of(Figure.EMPLOYER_COST, side, shown, listOf(it)) }
        val payslip = payslipOf(person, side)
        val terms = buildList {
            add(gross(person, side).asTerm(TermKind.GROSS))
            add(Term(TermKind.PAID_BY_ZUS, factorsOf(person, side)?.paidByZus ?: 0.0, Sign.MINUS))
            payslip.employerLines.forEach { add(Term(TermKind.EMPLOYER_LINE, it.amount, payLine = it.kind)) }
            add(Term(TermKind.EXPENSE_REFUNDS, person.expenseRefunds))
        }
        return Breakdown.of(Figure.EMPLOYER_COST, side, shown, terms)
    }

    /** The month's gross: everyone's. */
    fun totalGross(payroll: RosterPayroll): Breakdown =
        Breakdown.of(Figure.GROSS, Side.ACTUAL, payroll.totalGross, payroll.employees.map { personTerm(it, it.payslip.gross) })

    /** The month's take-home: everyone's. */
    fun totalTakeHome(payroll: RosterPayroll, side: Side = Side.ACTUAL): Breakdown = Breakdown.of(
        Figure.TAKE_HOME, side, if (side == Side.PLAN) payroll.totalPlannedToPay else payroll.totalToPay,
        payroll.employees.map { personTerm(it, if (side == Side.PLAN) it.plannedToPay else it.toPay) }
    )

    /**
     * The month's employer cost: the shifts' cost alone (what Finances counts) plus the month's
     * items on top (bonuses, overtime and supplements, leave pay, salaries, refunds), each per person.
     */
    fun totalEmployerCost(payroll: RosterPayroll, side: Side): Breakdown {
        val shown = if (side == Side.PLAN) payroll.totalPlannedEmployerCost else payroll.totalEmployerCost
        return Breakdown.of(
            Figure.EMPLOYER_COST, side, shown,
            listOf(shiftsCost(payroll, side).asTerm(TermKind.SHIFTS_EMPLOYER_COST), monthItems(payroll, side).asTerm(TermKind.MONTH_ITEMS))
        )
    }

    /** The shifts' employer cost alone (their cost snapshots), per person. */
    fun shiftsCost(payroll: RosterPayroll, side: Side): Breakdown {
        val shown = if (side == Side.PLAN) payroll.totalShiftsEmployerCostPlanned else payroll.totalShiftsEmployerCost
        return Breakdown.of(
            Figure.SHIFTS_EMPLOYER_COST, side, shown,
            payroll.employees.map { personTerm(it, if (side == Side.PLAN) it.shiftsEmployerCostPlanned else it.shiftsEmployerCost) }
        )
    }

    /** What the month's estimate adds on top of the shifts, per person: the employer cost less the shifts' cost. */
    fun monthItems(payroll: RosterPayroll, side: Side): Breakdown {
        val shown = if (side == Side.PLAN) payroll.totalPlannedEmployerCost - payroll.totalShiftsEmployerCostPlanned
            else payroll.totalEmployerCost - payroll.totalShiftsEmployerCost
        return Breakdown.of(
            Figure.MONTH_ITEMS, side, shown,
            payroll.employees.map { person ->
                val amount = if (side == Side.PLAN) person.plannedEmployerCost - person.shiftsEmployerCostPlanned
                    else person.employerCost - person.shiftsEmployerCost
                Term(TermKind.PERSON, kotlin.math.abs(amount), if (amount < 0) Sign.MINUS else Sign.PLUS, personId = person.userId)
            }
        )
    }

    private fun personTerm(person: EmployeePayroll, amount: Double): Term = when {
        person.missingPay -> Term(TermKind.PERSON_WITHOUT_PAY, 0.0, Sign.NOT_COUNTED, personId = person.userId, hours = person.plannedHours, shifts = person.shifts.size)
        else -> Term(TermKind.PERSON, amount, personId = person.userId, hours = person.paidHours.takeIf { it > 0 })
    }
}
